import type { TransactionCurrency } from '@contracts/payment/Currency';
import type { ChangeRequestList } from '@src/changeRequest/domain/ChangeRequestList';
import type { Client } from '@src/clients/domain/Client';
import { DeliveryTypeOption } from '@src/clients/domain/Client';
import type { Timestamp } from '@src/common/time/Timestamp';
import { getLastPartOfPath } from '@src/documents/hooks/useFindAllDocumentsForOrder';
import { FormFields } from '@src/orders/domain/CheckedFields';
import type { OrderActions } from '@src/orders/domain/OrderList';
import type {
  Document,
  EntryValue,
  PermitApplication,
} from '@src/orders/domain/PermitApplication';
import { isEntryValueEmpty } from '@src/orders/domain/PermitApplication';
import type { PermitSubmission } from '@src/orders/domain/PermitSubmission';
import type { PermitType } from '@src/orders/domain/PermitType';
import { fields } from '@src/orders/schemas/EditOrderFormInputs';
import { AxleFieldsType } from '@src/permitField/AxleInformationFields';
import {
  CompanyMailingAddressDetailsField,
  CompanyMailingAddressField,
  CompanyMailingCityField,
  CompanyMailingStateField,
  CompanyMailingZipCodeField,
  CompanyPhysicalAddressDetailsField,
  CompanyPhysicalAddressField,
  CompanyPhysicalCityField,
  CompanyPhysicalStateField,
  CompanyPhysicalZipCodeField,
} from '@src/permitField/CompanyAddressFields';
import {
  CarrierContactNameField,
  CompanyEmailField,
  CompanyFaxField,
  CompanyNameField,
  CompanyPhoneField,
  DoingBusinessAsField,
} from '@src/permitField/CompanyInformationSectionFields';
import { insuranceInformationFields } from '@src/permitField/InsuranceInformationFields';
import {
  NotificationEmailAddressField,
  NotificationMobilePhoneField,
  PrimaryDeliveryTypeField,
  PrimaryDeliveryTypeValueField,
  SecondaryDeliveryTypeField,
  SecondaryDeliveryTypeValueField,
} from '@src/permitField/PermitDeliveryAndNotificationsFields';
import { PONumberField } from '@src/permitField/PONumberField';
import {
  EffectiveDateField,
  TripBaseRegisteredWeightField,
  TripDestinationField,
  TripIncludesReturnTripField,
  TripMileageField,
  TripOriginField,
  TripReturnDestinationField,
  TripReturnMileageField,
  TripReturnRouteField,
  TripRouteField,
} from '@src/permitField/TripInformationFields';
import type { PermitFees } from '@src/permits/permitFees/domain/PermitFees';

import { PermitInstruction } from './PermitInstruction';

export enum OrderStatus {
  DRAFT = 'DRAFT',
  NEW = 'NEW',
  ACTIVE = 'ACTIVE',
  HOLD = 'HOLD',
  CANCELLED = 'CANCELLED',
  TO_INVOICE = 'TO_INVOICE',
  INVOICED = 'INVOICED',
  MODIFY_INVOICE = 'MODIFY_INVOICE',
  COMPLETED = 'COMPLETED',
  TO_REVIEW = 'TO_REVIEW',
  ARCHIVED = 'ARCHIVED',
  AUTOMATIC_DRAFT = 'AUTOMATIC_DRAFT',
}

interface Attributes {
  id: string;
  displayId: string;
  clientId: string;
  clientName: string;
  doingBusinessAs?: string;
  claimedBy?: string;
  status: OrderStatus;
  PONumber?: string;
  permitApplicationGroups: PermitGroup[];
  allowedActions: Set<OrderActions>;
  changeRequests?: ChangeRequestList;
}

export interface Accumulator {
  trips?: { [key: string]: EntryValue };

  [key: string]: EntryValue | { [key: string]: EntryValue } | undefined;
}

export interface PermitGroup {
  originalPermit: OrderPermitApplication;
  subsequentPermits: OrderPermitApplication[];
}

export type OrderPermitApplication = {
  application: PermitApplication;
  permitTypeName: string;
  permitType: string;
  permitTypeJurisdiction: string;
  permitTypeCurrency: TransactionCurrency;
  permitTypeJurisdictionUrl?: string;
  permitTypeIsSelfIssued: boolean;
  permitTypeIsActive: boolean;
  validateExpirationDate: boolean;
  validateExpirationTime: boolean;
  instructions?: string;
  submission?: PermitSubmission;
  permitStateDocument?: Document;
  documents?: Document[];
  fees?: PermitFees;
};

export type SubmittedPermitApplication = Omit<
  OrderPermitApplication,
  'submission'
> & {
  submission: PermitSubmission;
};

export const getStatePermitUploadFile = (
  permitApplication: SubmittedPermitApplication,
) => {
  return permitApplication.application.statePermitDocument
    ? {
        documentId:
          permitApplication.application.statePermitDocument.documentId,
        name: getLastPartOfPath(
          permitApplication.application.statePermitDocument.key,
        ),
        sendToClient:
          permitApplication.application.statePermitDocument.sendToClient,
        displayName:
          permitApplication.application.statePermitDocument.displayName,
      }
    : undefined;
};

export const independentTripFields: readonly string[] = [
  TripOriginField.fieldName,
  TripDestinationField.fieldName,
  TripBaseRegisteredWeightField.fieldName,
  TripRouteField.fieldName,
  TripMileageField.fieldName,
  TripIncludesReturnTripField.fieldName,
  TripReturnDestinationField.fieldName,
  TripReturnRouteField.fieldName,
  TripReturnMileageField.fieldName,
  EffectiveDateField.fieldName,
];

export class Order {
  public readonly id: string;
  public readonly displayId: string;
  public readonly clientId: string;
  public readonly clientName: string;
  public readonly doingBusinessAs?: string;
  public readonly claimedBy?: string;
  public readonly status: OrderStatus;
  public readonly PONumber?: string;
  public readonly permitApplicationGroups: PermitGroup[];
  public readonly allowedActions: Set<OrderActions>;
  public readonly changeRequests?: ChangeRequestList;

  constructor(attributes: Attributes) {
    this.id = attributes.id;
    this.displayId = attributes.displayId;
    this.clientId = attributes.clientId;
    this.doingBusinessAs = attributes.doingBusinessAs;
    this.clientName = attributes.clientName;
    this.claimedBy = attributes.claimedBy;
    this.status = attributes.status;
    this.PONumber = attributes.PONumber;
    this.permitApplicationGroups = attributes.permitApplicationGroups;
    this.allowedActions = attributes.allowedActions;
    this.changeRequests = attributes.changeRequests;
  }

  getLatestPermitQuantityMatching(permitTypes: PermitType[]) {
    return permitTypes.map((permitType, index) => {
      const matchingApplications =
        this.getAllLatestEffectivePermitApplicationsSortedByUpdatedAt().filter(
          (application) =>
            application.application.permitTypeOriginalId ===
            permitType.originalId,
        );

      return {
        type: permitType,
        quantity: matchingApplications.length || 0,
        index,
      };
    });
  }

  getAllLatestEffectivePermitApplicationsSortedByUpdatedAt(): OrderPermitApplication[] {
    return this.getLatestEffectivePermitApplications((a, b) =>
      b.application.updatedAt
        .format()
        .localeCompare(a.application.updatedAt.format()),
    );
  }

  getAllLatestEffectivePermitApplicationsSortedByEntriesUpdatedAt(): OrderPermitApplication[] {
    return this.getLatestEffectivePermitApplications((a, b) =>
      b.application.entriesUpdatedAt
        .format()
        .localeCompare(a.application.entriesUpdatedAt.format()),
    );
  }

  private getLatestEffectivePermitApplications(
    sortBy: (a: OrderPermitApplication, b: OrderPermitApplication) => number,
  ): OrderPermitApplication[] {
    return this.permitApplicationGroups
      .filter(({ originalPermit, subsequentPermits }) => {
        if (subsequentPermits.length === 0) {
          return originalPermit.application.isEffective();
        }
        const sortedPermits = subsequentPermits.sort(sortBy);
        return sortedPermits[0].application.isEffective();
      })
      .map(({ originalPermit, subsequentPermits }) => {
        if (subsequentPermits.length === 0) {
          return originalPermit;
        }
        const sortedPermits = subsequentPermits.sort(sortBy);
        return sortedPermits[0];
      })
      .sort(sortBy);
  }

  getAllLatestEffectivePermitApplicationsOrderedByIndex(): OrderPermitApplication[] {
    return this.getLatestEffectivePermitApplications(
      (a, b) => b.application.permitIndex - a.application.permitIndex,
    ).sort((a, b) => a.application.permitIndex - b.application.permitIndex);
  }

  findSubmittedApplicationOrThrow(
    permitApplicationId: string,
  ): SubmittedPermitApplication {
    const allPermits = this.permitApplicationGroups.flatMap((group) => [
      group.originalPermit,
      ...group.subsequentPermits,
    ]);
    const found = allPermits.find(
      (application) => application.application.id === permitApplicationId,
    );
    if (!found) {
      //Should only happen with manual navigation through the url
      throw Error(
        'Requested permit application is not part of requested order',
      );
    }
    if (!found.submission) {
      throw Error('Requested permit application is not submitted');
    }
    return found as SubmittedPermitApplication;
  }

  getFields() {
    const checkedFields = [...this.getAllOrderFields()];

    return FormFields.convertToFormFields({ checkedFields });
  }

  getAllOrderFields() {
    return this.getAllLatestEffectivePermitApplicationsSortedByUpdatedAt().reduce(
      (acc: Set<string>, application) => {
        application.application.fields.forEach((field) => {
          acc.add(field);
        });
        return acc;
      },
      new Set<string>(),
    );
  }

  getAllOrderFieldsExcluding(excludedPermitApplicationIds: string[]) {
    return this.getAllLatestEffectivePermitApplicationsSortedByUpdatedAt()
      .filter(
        (application) =>
          !excludedPermitApplicationIds.includes(application.application.id),
      )
      .reduce((acc: Set<string>, application) => {
        application.application.fields.forEach((field) => {
          acc.add(field);
        });
        return acc;
      }, new Set<string>());
  }

  getLastUpdatedFor(field: string, permitApplicationId?: string) {
    return this.changeRequests?.getLastChangeDateFor(
      field,
      permitApplicationId,
    );
  }

  getMostRecentEntries(client: Client) {
    const latestChangeRequest = this.changeRequests?.getLatestChangeRequest();
    const entries = latestChangeRequest
      ? this.mergeExistingPermitWith(latestChangeRequest.form)
      : this.getEntriesFromPermitApplications();

    entries[PONumberField.fieldName] = this.PONumber;
    return prefillClientInfo(entries, client);
  }

  private mergeExistingPermitWith(
    changeRequestForm: Record<string, unknown>,
  ): Accumulator {
    const entriesFromPermits = this.getEntriesFromPermitApplications();
    const entries = { ...changeRequestForm } as Accumulator;

    if (entriesFromPermits.trips || entries.trips) {
      entries.trips = {
        ...entriesFromPermits.trips,
        ...entries.trips,
      };
    }

    return entries;
  }

  private getEntriesFromPermitApplications(): Accumulator {
    return this.getAllLatestEffectivePermitApplicationsSortedByEntriesUpdatedAt()
      .reverse()
      .reduce<Accumulator>(
        (form, { application }) =>
          accumulatePermitToForm({
            form,
            permit: {
              id: application.id,
              entries: application.getEntries(),
              effectiveDate: application.effectiveDate,
            },
          }),
        {},
      );
  }

  getPermitsInstructions(): PermitInstruction[] {
    return this.getAllLatestEffectivePermitApplicationsSortedByUpdatedAt().reduce(
      (acc, current) => {
        if (current.instructions) {
          const instructions = new PermitInstruction({
            jurisdiction: current.permitTypeJurisdiction,
            permit: {
              name: current.permitTypeName,
              type: current.permitType,
              currency: current.permitTypeCurrency,
            },
            text: current.instructions,
          });
          acc.push(instructions);
        }
        return acc;
      },
      [] as PermitInstruction[],
    );
  }

  canDo(action: OrderActions): boolean {
    return this.allowedActions.has(action);
  }

  getFieldsInDraft(): FormFields {
    const latestEffectivePermits =
      this.getAllLatestEffectivePermitApplicationsSortedByUpdatedAt().map(
        (permit) => permit.application,
      );

    const fieldsInNewPermits = FormFields.convertToFormFields({
      checkedFields: latestEffectivePermits.reduce(
        (fields: string[], permit) =>
          permit.isNew() ? [...fields, ...permit.fields] : fields,
        [],
      ),
    });
    const fieldsInNotNewPermits = FormFields.convertToFormFields({
      checkedFields: latestEffectivePermits.reduce(
        (fields: string[], permit) =>
          !permit.isNew() ? [...fields, ...permit.fields] : fields,
        [],
      ),
    });

    fieldsInNewPermits.filter(fieldsInNotNewPermits);
    fieldsInNewPermits.applyEnforceDeliveryTypeFields();
    return fieldsInNewPermits;
  }

  getFieldsHavingValuesInOtherPermits(
    excludedPermitId: string,
    client: Client,
  ): string[] {
    const otherPermitsFields = this.getAllOrderFieldsExcluding([
      excludedPermitId,
    ]);
    const fieldsToCheck = [...otherPermitsFields, ...AxleFieldsType];
    const fieldsWithValue = fieldsToCheck.filter((fieldName) =>
      this.hasFieldValueInOtherPermits(fieldName, excludedPermitId),
    );
    const tripFieldsWithValue = this.getTripFieldsWithValueForOtherPermits(
      excludedPermitId,
      client,
    );
    return [...fieldsWithValue, ...tripFieldsWithValue];
  }

  private getTripFieldsWithValueForOtherPermits(
    excludesPermitId: string,
    client: Client,
  ): string[] {
    const entries = this.getMostRecentEntries(client);
    const trips = entries.trips as
      | Record<string, Record<string, unknown>>
      | undefined;

    const otherPermits =
      this.getAllLatestEffectivePermitApplicationsSortedByUpdatedAt().filter(
        (app) => app.application.id !== excludesPermitId,
      );

    return otherPermits.flatMap((permit) =>
      independentTripFields
        .filter((field) => this.permitHasTripFieldValue(permit, field, trips))
        .map((field) => `trips.${permit.application.id}.${field}`),
    );
  }

  private hasFieldValueInOtherPermits(
    fieldName: string,
    excludedPermitId: string,
  ): boolean {
    const otherPermits =
      this.getAllLatestEffectivePermitApplicationsSortedByUpdatedAt().filter(
        (app) => app.application.id !== excludedPermitId,
      );

    return otherPermits.some((permit) =>
      permit.application.hasValueForField(fieldName),
    );
  }

  private permitHasTripFieldValue(
    permit: OrderPermitApplication,
    field: string,
    trips: Record<string, Record<string, unknown>> | undefined,
  ): boolean {
    if (
      !permit.application.hasField(field) ||
      !trips ||
      !trips[permit.application.id]
    ) {
      return false;
    }

    const value = trips[permit.application.id][field];
    return value !== undefined;
  }
}

const prefillDeliveryTypes = (acc: Accumulator, client: Client) => {
  const hasPrimaryDeliveryType =
    acc[PrimaryDeliveryTypeField.fieldName] &&
    acc[PrimaryDeliveryTypeField.fieldName] !== '';

  const primaryDeliveryType = hasPrimaryDeliveryType
    ? acc[PrimaryDeliveryTypeField.fieldName]
    : client.primaryDeliveryType.deliveryType;

  const primaryDeliveryIsWebDownload =
    primaryDeliveryType === DeliveryTypeOption.WEB_DOWNLOAD;

  const hasPrimaryDeliveryTypeValue =
    hasPrimaryDeliveryType && !!acc[PrimaryDeliveryTypeValueField.fieldName];

  const primaryDeliveryTypeValue = primaryDeliveryIsWebDownload
    ? undefined
    : hasPrimaryDeliveryTypeValue
      ? acc[PrimaryDeliveryTypeValueField.fieldName]
      : client.primaryDeliveryType.value;

  const hasSecondaryDeliveryType =
    acc[SecondaryDeliveryTypeField.fieldName] &&
    acc[SecondaryDeliveryTypeField.fieldName] !== '';

  const secondaryDeliveryType = hasSecondaryDeliveryType
    ? acc[SecondaryDeliveryTypeField.fieldName]
    : client.secondaryDeliveryType?.deliveryType;

  const secondaryDeliveryIsWebDownload =
    secondaryDeliveryType === DeliveryTypeOption.WEB_DOWNLOAD;

  const hasSecondaryDeliveryTypeValue =
    hasSecondaryDeliveryType &&
    !!acc[SecondaryDeliveryTypeValueField.fieldName];

  const secondaryDeliveryTypeValue = secondaryDeliveryIsWebDownload
    ? undefined
    : hasSecondaryDeliveryTypeValue
      ? acc[SecondaryDeliveryTypeValueField.fieldName]
      : client.secondaryDeliveryType?.value;

  return {
    primaryDeliveryType,
    primaryDeliveryTypeValue,
    secondaryDeliveryType,
    secondaryDeliveryTypeValue,
  };
};

const prefillInsuranceInformation = (acc: Accumulator, client: Client) => {
  const insuranceFields = Object.keys(insuranceInformationFields);
  const result: Record<string, EntryValue> = {};

  for (const fieldName of insuranceFields) {
    const hasOrderValue = acc[fieldName] && acc[fieldName] !== '';
    const value = hasOrderValue
      ? acc[fieldName]
      : client.insuranceInformation?.[
          fieldName as keyof typeof client.insuranceInformation
        ];

    if (value !== undefined && value !== '') {
      result[fieldName] = value;
    }
  }

  return result;
};

const prefillClientInfo = (acc: Accumulator, client: Client): Accumulator => {
  const jurisdictionIds = client.jurisdictionIdNumbers.reduce(
    (idsAccumulator, { field, value }) => {
      return {
        ...idsAccumulator,
        [field]: !acc[field] ? value : acc[field],
      };
    },
    {},
  );

  const {
    primaryDeliveryType,
    primaryDeliveryTypeValue,
    secondaryDeliveryType,
    secondaryDeliveryTypeValue,
  } = prefillDeliveryTypes(acc, client);
  return {
    ...acc,
    [CompanyNameField.fieldName]: client.name,
    [DoingBusinessAsField.fieldName]: client.doingBusinessAs,
    [CompanyPhoneField.fieldName]: client.primaryContact.phone,
    [CompanyFaxField.fieldName]: client.fax,
    [CompanyEmailField.fieldName]: client.primaryContact.email,
    [CarrierContactNameField.fieldName]: client.primaryContact.name,
    [CompanyPhysicalAddressField.fieldName]: client.address.address,
    [CompanyPhysicalAddressDetailsField.fieldName]:
      client.address.addressDetails,
    [CompanyPhysicalCityField.fieldName]: client.address.city,
    [CompanyPhysicalStateField.fieldName]: client.address.state,
    [CompanyPhysicalZipCodeField.fieldName]: client.address.zipCode,
    [CompanyMailingAddressField.fieldName]: client.mailingAddress.address,
    [CompanyMailingAddressDetailsField.fieldName]:
      client.mailingAddress.addressDetails,
    [CompanyMailingCityField.fieldName]: client.mailingAddress.city,
    [CompanyMailingStateField.fieldName]: client.mailingAddress.state,
    [CompanyMailingZipCodeField.fieldName]: client.mailingAddress.zipCode,
    [PrimaryDeliveryTypeField.fieldName]: primaryDeliveryType,
    [PrimaryDeliveryTypeValueField.fieldName]: primaryDeliveryTypeValue,
    [SecondaryDeliveryTypeField.fieldName]: secondaryDeliveryType,
    [SecondaryDeliveryTypeValueField.fieldName]: secondaryDeliveryTypeValue,
    [NotificationEmailAddressField.fieldName]:
      client.permitNotifications?.email ?? '',
    [NotificationMobilePhoneField.fieldName]:
      client.permitNotifications?.phone ?? '',
    ...jurisdictionIds,
    ...prefillInsuranceInformation(acc, client),
  };
};

export const getFieldKeysInCategory = (category: string) => {
  const sections = fields[category];
  return Object.keys(sections).flatMap((sectionKey) =>
    Object.keys(sections[sectionKey]),
  );
};

export const accumulatePermitToForm = ({
  form,
  permit,
}: {
  form: Accumulator;
  permit: {
    id: string;
    entries: Record<string, EntryValue>;
    effectiveDate: Timestamp;
  };
}) => {
  const entries = permit.entries;
  const effectiveDate = permit.effectiveDate;

  const aggregatedEntry: Record<string, EntryValue> = {};

  for (const [key, value] of Object.entries({
    ...entries,
    [EffectiveDateField.fieldName]: effectiveDate.getDate(),
  })) {
    if (independentTripFields.includes(key)) {
      aggregatedEntry[key] = value;
    } else {
      accumulateEntryToFormIsApplicable({ form, key, value });
    }
  }

  if (Object.keys(aggregatedEntry).length > 0) {
    if (!form.trips) {
      form.trips = {};
    }

    form.trips[permit.id] = aggregatedEntry;
  }

  return form;
};

const accumulateEntryToFormIsApplicable = ({
  form,
  key,
  value,
}: {
  form: Accumulator;
  key: string;
  value: EntryValue;
}) => {
  if (!form[key]) {
    form[key] = value;
    return;
  }

  if (isEntryValueEmpty(value)) {
    return;
  }

  if (typeof value !== 'object') {
    form[key] = value;
    return;
  }

  if (Array.isArray(value)) {
    for (let index = 0; index < value.length; index++) {
      accumulateEntryToFormIsApplicable({
        form: form[key] as Accumulator,
        key: index.toString(),
        value: value[index],
      });
    }
    return;
  }

  for (const subKey in value) {
    accumulateEntryToFormIsApplicable({
      form: form[key] as Accumulator,
      key: subKey,
      value: (value as Record<string, EntryValue>)[subKey],
    });
  }
};
